import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { useNavigate } from "react-router-dom";
import { Plus, X, Search, Building2, Pencil, LayoutGrid, List } from "lucide-react";
import { ExportButton } from "@/components/ExportButton";
import { DeleteButton } from "@/components/DeleteButton";
import { BulkBar, RowCheckbox, bulkDelete } from "@/components/BulkBar";

export default function Customers() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("list");
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await api.get(`/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setList(r.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
   
  useEffect(() => { load(); }, []);

  const ids = list.map((c) => c.id);
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  const someSelected = ids.some((id) => selected.has(id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) ids.forEach((id) => n.delete(id));
    else ids.forEach((id) => n.add(id));
    return n;
  });
  const clearSel = () => setSelected(new Set());
  const doBulkDelete = async () => {
    const idsArr = Array.from(selected);
    if (!window.confirm(`Delete ${idsArr.length} customer(s) and their equipment? This cannot be undone.`)) return;
    setBulkBusy(true);
    const ok = await bulkDelete(idsArr, (id) => api.delete(`/customers/${id}`));
    setBulkBusy(false); clearSel(); await load();
    if (ok < idsArr.length) setErr(`${idsArr.length - ok} deletion(s) failed.`);
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex items-end justify-between">
        <div>
          <p className="label-eyebrow text-primary">Directory</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Customers</h1>
          <p className="mt-1 text-sm text-muted-foreground">Companies, contacts and locations you service.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-border/70">
            <button data-testid="customers-view-list" onClick={() => setView("list")}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>
              <List className="h-3.5 w-3.5" /> List
            </button>
            <button data-testid="customers-view-cards" onClick={() => setView("cards")}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium ${view === "cards" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>
              <LayoutGrid className="h-3.5 w-3.5" /> Cards
            </button>
          </div>
          <button data-testid="customers-create-button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90">
            <Plus className="h-3.5 w-3.5" /> New customer
          </button>
          <ExportButton resource="customers" testid="customers-export" />
        </div>
      </header>

      <div className="mb-4 flex items-center gap-2">
        <label className="inline-flex items-center gap-2 rounded-md border border-border/70 px-3 py-2 text-xs">
          <RowCheckbox testid="customers-select-all" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} />
          Select all
        </label>
        <div className="relative flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            data-testid="customers-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by company name…"
            className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm"
          />
        </div>
        <button onClick={load} className="rounded-md border border-border/70 px-3 py-2 text-xs font-medium">Search</button>
      </div>

      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      {view === "list" ? (
        <div className="sharp-card overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-secondary/30 text-left">
              <tr>
                <th className="w-10 px-4 py-2">
                  <RowCheckbox testid="customers-list-select-all" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} />
                </th>
                <th className="px-4 py-2 label-eyebrow">Company</th>
                <th className="px-4 py-2 label-eyebrow">Contact</th>
                <th className="px-4 py-2 label-eyebrow">Email</th>
                <th className="px-4 py-2 label-eyebrow">Mobile</th>
                <th className="px-4 py-2 label-eyebrow">Location</th>
                <th className="px-4 py-2 label-eyebrow">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`}
                  onClick={() => navigate(`/app/customers/${c.id}`)}
                  className="cursor-pointer border-b border-border/40 hover:bg-secondary/40">
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    <RowCheckbox testid={`customer-select-${c.id}`} checked={selected.has(c.id)} onChange={() => toggleOne(c.id)} />
                  </td>
                  <td className="px-4 py-2">
                    <span className="inline-flex items-center gap-2 font-medium"><Building2 className="h-3.5 w-3.5 text-primary" />{c.company_name}</span>
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{c.contact_person || "—"}</td>
                  <td className="px-4 py-2 max-w-[200px] truncate text-xs text-muted-foreground">{c.email || "—"}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{c.mobile || "—"}</td>
                  <td className="px-4 py-2 max-w-[200px] truncate text-xs text-muted-foreground">{[c.city, c.state].filter(Boolean).join(", ") || c.address || "—"}</td>
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1">
                      <button data-testid={`customer-edit-${c.id}`} onClick={() => setEditing(c)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
                      <DeleteButton
                        testid={`customer-delete-${c.id}`}
                        confirmText={`Delete ${c.company_name} and all its equipment? This cannot be undone.`}
                        onDelete={async () => { try { await api.delete(`/customers/${c.id}`); await load(); } catch (e) { setErr(formatApiError(e)); } }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-xs text-muted-foreground">No customers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((c) => (
          <div key={c.id} className={`sharp-card relative p-4 text-left transition-all duration-150 hover:-translate-y-0.5 hover:border-primary/50 ${selected.has(c.id) ? "ring-2 ring-primary/60" : ""}`}>
            <div className="absolute right-3 top-3">
              <RowCheckbox testid={`customer-select-${c.id}`} checked={selected.has(c.id)} onChange={() => toggleOne(c.id)} />
            </div>
            <button
              data-testid={`customer-card-${c.id}`}
              onClick={() => navigate(`/app/customers/${c.id}`)}
              className="block w-full text-left"
            >
              <div className="flex items-center gap-3 pr-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Building2 className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-heading text-base font-semibold">{c.company_name}</p>
                  <p className="truncate text-xs text-muted-foreground">{c.contact_person || "—"}</p>
                </div>
              </div>
              <div className="mt-4 space-y-1 text-xs text-muted-foreground">
                <p className="truncate">{c.email || "—"}</p>
                <p>{c.mobile || "—"}</p>
                <p className="truncate">{[c.city, c.state].filter(Boolean).join(", ") || c.address || "—"}</p>
              </div>
            </button>
            <div className="mt-3 flex justify-end gap-2">
              <button
                data-testid={`customer-edit-${c.id}`}
                onClick={() => setEditing(c)}
                className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground hover:border-primary/60 hover:text-primary"
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <DeleteButton
                testid={`customer-delete-${c.id}`}
                confirmText={`Delete ${c.company_name} and all its equipment? This cannot be undone.`}
                onDelete={async () => { try { await api.delete(`/customers/${c.id}`); await load(); } catch (e) { setErr(formatApiError(e)); } }}
              />
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="col-span-full text-sm text-muted-foreground">No customers yet.</p>}
      </div>
      )}

      <BulkBar count={selected.size} entityLabel="customers" onClear={clearSel} onDelete={doBulkDelete} busy={bulkBusy} />

      {creating && <CustomerModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <CustomerModal existing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CustomerModal({ onClose, onSaved, existing }) {
  const [form, setForm] = useState({
    company_name: existing?.company_name || "", contact_person: existing?.contact_person || "",
    email: existing?.email || "", mobile: existing?.mobile || "", gst: existing?.gst || "",
    address: existing?.address || "", city: existing?.city || "", state: existing?.state || "",
    pincode: existing?.pincode || "", notes: existing?.notes || "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (existing) await api.patch(`/customers/${existing.id}`, form);
      else await api.post("/customers", form);
      onSaved();
    }
    catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-2xl sharp-card">
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold">{existing ? `Edit customer — ${existing.company_name}` : "New customer"}</h3>
          <button onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5 sm:grid-cols-2">
          {[
            ["company_name", "Company name *", true, true],
            ["contact_person", "Contact person"],
            ["email", "Email"],
            ["mobile", "Mobile"],
            ["gst", "GSTIN"],
            ["address", "Address", false, true],
            ["city", "City"], ["state", "State"], ["pincode", "Pincode"],
          ].map(([k, l, req, full]) => (
            <div key={k} className={full ? "sm:col-span-2" : ""}>
              <label className="label-eyebrow mb-1.5 block">{l}</label>
              <input data-testid={`customer-form-${k}`} required={!!req} value={form[k]} onChange={set(k)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
          ))}
          {err && <div className="sm:col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="customer-form-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : (existing ? "Save changes" : "Create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
